import {
  BoardAddIcon,
  BoxLogoIcon,
  BrandfolderLogoIcon,
  CantoIcon,
  DropboxIcon,
  FolderUpIcon,
  FrameIoIcon,
  GoogleDriveIcon,
  InboxIcon,
  PlusIcon,
  SharePointIcon,
  UploadIcon,
} from '@air/design-icons';
import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';

import { getMenuOption, type ImportOptions } from '~/constants/MenuOptions';
import { BOX_ICON_COLORS, DROPBOX_ICON_COLORS } from '~/constants/vendorLogoColors';
import { type ImportSourceType } from '~/utils/ImportUtils';

export type getAddMenuOptionsParams = {
  onImportFrom?: (importOption: ImportOptions) => void;
  onAddSubBoard?: () => void;
  onCreateBoard?: () => void;
  onCreateCanvas?: () => void;
  onCollectContent?: () => void;
  onUploadFilesFromComputer?: () => void;
  onUploadFoldersFromComputer?: () => void;
  exposeImportSources: ImportSourceType[];
};

export const getAddMenuOptions = ({
  onAddSubBoard,
  onCreateBoard,
  onCreateCanvas,
  onImportFrom,
  onUploadFilesFromComputer,
  onUploadFoldersFromComputer,
  onCollectContent,
  exposeImportSources,
}: getAddMenuOptionsParams) => {
  const options: DropdownMenuOption[] = [];

  if (onUploadFilesFromComputer) {
    options.push({
      id: 'upload-files',
      label: 'File Upload',
      onSelect: onUploadFilesFromComputer,
      prefix: <UploadIcon size="sm" />,
      type: 'item',
    });
  }

  if (onUploadFoldersFromComputer) {
    options.push({
      id: 'upload-folders',
      label: 'Folder Upload',
      onSelect: onUploadFoldersFromComputer,
      prefix: <FolderUpIcon size="sm" />,
      type: 'item',
    });
  }

  if (onCollectContent) {
    if (options.length > 0) {
      options.push({ id: 'cc-separator', type: 'separator' });
    }
    options.push({
      id: 'content-collection',
      label: 'Collect Content',
      description: 'Collect content directly from anyone, no Air account needed.',
      onSelect: onCollectContent,
      prefix: <InboxIcon size="sm" />,
      type: 'item',
    });
  }

  if (onImportFrom && exposeImportSources.length > 0) {
    if (options.length > 0) {
      options.push({
        id: 'import-seperator',
        type: 'separator',
      });
    }

    options.push({
      id: 'import-title',
      label: 'Import from...',
      type: 'title',
    });

    exposeImportSources.forEach((source) => {
      const id: ImportOptions = source === 'googleDrive' ? 'import-from-drive' : `import-from-${source}`;
      const option = getMenuOption(id);
      options.push({
        id,
        label: option?.label,
        onSelect: () => onImportFrom(id),
        prefix: getImportLogo(source),
        type: 'item',
      });
    });
  }

  if (onAddSubBoard) {
    if (options.length > 0) {
      options.push({
        id: 'add-sub-board-seperator',
        type: 'separator',
      });
    }

    options.push({
      id: 'add-sub-board',
      label: 'Add Sub-board',
      onSelect: onAddSubBoard,
      prefix: <BoardAddIcon size="sm" />,
      type: 'item',
    });
  }

  if (onCreateBoard) {
    if (options.length > 0) {
      options.push({
        id: 'create-board-seperator',
        type: 'separator',
      });
    }

    options.push({
      id: 'create-new-board',
      label: 'Create new board',
      onSelect: onCreateBoard,
      prefix: <BoardAddIcon size="sm" />,
      type: 'item',
    });
  }

  if (onCreateCanvas) {
    if (options.length > 0) {
      options.push({
        id: 'create-canvas-separator',
        type: 'separator',
      });
    }

    options.push({
      id: 'create-new-canvas',
      label: 'Create new Canvas',
      onSelect: onCreateCanvas,
      prefix: <PlusIcon size="sm" />,
      type: 'item',
    });
  }

  return options;
};

export const getImportLogo = (source: ImportSourceType) => {
  switch (source) {
    case 'googleDrive':
      return <GoogleDriveIcon size="sm" />;
    case 'box':
      return <BoxLogoIcon color={BOX_ICON_COLORS.hex} size="sm" />;
    case 'dropbox':
      return <DropboxIcon color={DROPBOX_ICON_COLORS.hex} size="sm" />;
    case 'brandfolder':
      return <BrandfolderLogoIcon size="sm" />;
    case 'canto':
      return <CantoIcon size="sm" />;
    case 'sharePoint':
      return <SharePointIcon size="sm" />;
    case 'frameio':
      return <FrameIoIcon size="sm" />;
    default:
      break;
  }
};
